/**
 * Constantes para el módulo de Login
 * Diseño profesional con Material UI
 */

/**
 * Estilos para el contenedor principal con fondo
 */
export const MAIN_CONTAINER_STYLES = (backgroundImage) => ({
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: "#0a0e27",
  overflow: "hidden",
  
  // Fondo con imagen blur
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    filter: "blur(8px) brightness(0.4)",
    transform: "scale(1.05)",
    zIndex: 0,
  },

  // Overlay con gradiente
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(10,14,39,0.85) 0%, rgba(18,18,35,0.9) 100%)",
    zIndex: 1,
  },

  // Patrón de puntos sutil
  backgroundImage: `radial-gradient(circle at 20% 50%, rgba(25, 118, 210, 0.05) 0%, transparent 50%),
                     radial-gradient(circle at 80% 80%, rgba(156, 39, 176, 0.05) 0%, transparent 50%)`,
});

/**
 * Estilos para el panel izquierdo (Branding)
 */
export const LEFT_PANEL_STYLES = {
  height: "100%",
  minWidth: "1000px",
  background: "transparent",
  borderRadius: { xs: 0, md: "24px 0 0 24px" },
  p: { xs: 4, md: 6 },
  position: "relative",
  zIndex: 2,
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(156, 39, 176, 0.08) 100%)",
    borderRadius: "inherit",
    zIndex: -1,
  },
};

/**
 * Estilos para el panel derecho (Formulario)
 */
export const RIGHT_PANEL_STYLES = {
  height: "100%",
  background: "linear-gradient(145deg, rgba(30, 30, 45, 0.95) 0%, rgba(20, 20, 30, 0.98) 100%)",
  backdropFilter: "blur(20px) saturate(180%)",
  borderRadius: { xs: 3, md: "0 24px 24px 0" },
  p: { xs: 4, md: 5 },
  position: "relative",
  zIndex: 2,
  border: "1px solid rgba(255, 255, 255, 0.08)",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 1px rgba(255, 255, 255, 0.1) inset",

  // Efecto de brillo sutil en el borde
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: "linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.5), transparent)",
    borderRadius: "inherit",
  },
};

/**
 * Estilos para el contenedor del logo
 */
export const LOGO_CONTAINER_STYLES = {
  textAlign: "left",
};

/**
 * Estilos mejorados para los inputs del formulario
 */
export const INPUT_STYLES = {
  "& .MuiFilledInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    borderRadius: "12px",
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    fontSize: "0.95rem",

    "&:before, &:after": {
      display: "none",
    },

    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderColor: "rgba(255, 255, 255, 0.2)",
    },

    "&.Mui-focused": {
      backgroundColor: "rgba(25, 118, 210, 0.08)",
      borderColor: "#1976d2",
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.15), 0 4px 12px rgba(25, 118, 210, 0.2)",
      transform: "translateY(-2px)",
    },

    "&.Mui-disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.02)",
      opacity: 0.6,
    },
  },

  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.95rem",
    fontWeight: 500,
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#64b5f6",
    fontWeight: 600,
  },

  "& .MuiInputAdornment-root": {
    marginRight: 1,
  },
};

/**
 * Estilos para el botón de submit con diseño moderno
 */
export const BUTTON_STYLES = {
  py: 1.75,
  px: 3,
  fontSize: "1rem",
  fontWeight: 700,
  borderRadius: "12px",
  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)",
  textTransform: "none",
  boxShadow: "0 8px 24px rgba(25, 118, 210, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  letterSpacing: "0.5px",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    transition: "left 0.5s",
  },

  "&:hover": {
    background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 50%, #01579b 100%)",
    boxShadow: "0 12px 32px rgba(25, 118, 210, 0.5), 0 8px 16px rgba(0, 0, 0, 0.3)",
    transform: "translateY(-2px)",

    "&::before": {
      left: "100%",
    },
  },

  "&:active": {
    transform: "translateY(0px)",
    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
  },

  "&:disabled": {
    background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
    color: "rgba(255, 255, 255, 0.3)",
    boxShadow: "none",
  },
};

